import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import { Box, useTheme, Typography, Theme, IconButton, ClickAwayListener } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';

import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ListIcon from '@mui/icons-material/List';
import ThreePIcon from '@mui/icons-material/ThreeP';
// import BarChartIcon from '@mui/icons-material/BarChart';
import MenuIcon from '@mui/icons-material/Menu';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';


import useWindowWidth from '../hooks/useWindowWidth';
import PermissionGuard from './PermissionGuard';
import ChangelogPopup from './ChangelogPopup';
import { getVersion } from '../api/versionsApi/versionsApi';

type sidebarLinks = {
  linkName: string;
  path: string;
  icon: JSX.Element;
  separator?: boolean;
  resource?: string; // Maps to permission resource name
};

const menuItems: sidebarLinks[] = [
  {
    linkName: 'Dashboard',
    path: 'dashboard',
    icon: <DashboardIcon />,
    resource: 'Dashboard',
  },
  {
    linkName: 'Cases',
    path: 'cases',
    icon: <GavelIcon />,
    resource: 'Cases',
  },
  {
    linkName: 'Requests',
    path: 'requests',
    icon: <AssignmentTurnedInIcon />,
    resource: 'Requests',
  },
  {
    linkName: 'Payments',
    path: 'payments',
    icon: <ReceiptIcon />,
    separator: true,
    resource: 'Payments',
  },
  {
    linkName: 'Clients',
    path: 'clients',
    icon: <FolderSharedIcon />,
    resource: 'Clients',
  },
  {
    linkName: 'Defendants',
    path: 'defendants',
    icon: <FingerprintIcon />,
    separator: true,
    resource: 'Defendants',
  },
  {
    linkName: 'Case Types',
    path: 'case-types',
    icon: <CategoryIcon />,
    resource: 'Case Types',
  },
  {
    linkName: 'Questionnaires',
    path: 'questionnaires',
    icon: <DynamicFormIcon />,
    resource: 'Questionnaires',
  },
  {
    linkName: 'Snippets',
    path: 'snippets',
    icon: <TextSnippetIcon />,
    separator: true,
    resource: 'Snippets',
  },
  {
    linkName: 'Data File Manager',
    path: 'file-manager',
    icon: <ListIcon />,
    resource: 'Data File Manager',
  },
  {
    linkName: 'Banking',
    path: 'banking',
    icon: <AttachMoneyIcon />,
    separator: true,
    resource: 'Banking',
  },
  {
    linkName: 'Vendors',
    path: 'vendors',
    icon: <PeopleIcon />,
    resource: 'Vendors',
  },
  {
    linkName: 'Communications',
    path: 'communications',
    icon: <ThreePIcon />,
    resource: 'Communications',
  },
  {
    linkName: 'Users',
    path: 'users',
    icon: <PersonIcon />,
    resource: 'Users',
  },
  {
    linkName: 'Activity',
    path: 'activity',
    icon: <TimelineIcon />,
    resource: 'Activity',
  },
  // {
  //   linkName: 'Financial',
  //   path: 'financial',
  //   icon: <BarChartIcon />,
  // },
];

const useStyles = makeStyles<{ isMenuOpen?: boolean; isSidebarCollapsed?: boolean }>()(
  (theme: Theme, { isMenuOpen, isSidebarCollapsed }) => ({
    sideBarExpanded: {
      maxWidth: '155px',
      width: '100%',
      flexShrink: 0,
    },
    sideBarCollapsed: {
      maxWidth: '48px',
      width: '100%',
      flexShrink: 0,
    },
    activeLink: {
      color: theme.palette.common.white,
      display: isSidebarCollapsed ? 'none' : 'block',
      fontSize: '16px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
    defaultLink: {
      color: theme.palette.text.secondary,
      display: isSidebarCollapsed ? 'none' : 'block',
      fontSize: '16px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
    iconWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '24px',
      height: '24px',
      background: theme.palette.text.disabled,
      borderRadius: '100%',
      '& svg': {
        fontSize: '16px',
        color: theme.palette.primary.light,
      },
    },
    activeItemIconWrapper: {
      background: theme.palette.common.white,
    },
    menuItem: {
      padding: '12px',
      borderRadius: '12px',
      transition: 'padding 0.5s ease',

      '& :hover': {
        cursor: 'pointer',
      },
    },
    activeMenuItem: {
      background: 'rgba(255,255,255,0.19)',
    },
    linkHover: {
      '& :hover': {
        backgroundColor: 'rgba(255,255,255,0.19)',
        cursor: 'pointer',
        '& div': {
          background: theme.palette.common.white,
        },
        '& p': {
          background: 'transparent',
          color: theme.palette.common.white,
        },
        '& svg': {
          background: 'transparent',
        },
      },
    },
    linkHoverSeparator: {
      borderBottom: '1px solid rgba(118, 120, 133, 0.5)',
      paddingBottom: 8,
      marginBottom: 4,
    },
    sideBarWrapper: {
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 8px',
      backgroundColor: theme.palette.primary.light,
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '5px',
        height: '5px',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 20,
        backgroundColor: theme.palette.secondary.dark,
      },

      '@media (max-width: 550px)': {
        padding: 0,
        position: 'fixed',
        overflow: 'auto',
        width: '100%',
        zIndex: 2,
        ...(isMenuOpen && { paddingBottom: '16px', height: '100dvh' }),
      },
    },
    sideBarMainMenuGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      // marginTop: '56px',

      '@media (max-width: 550px)': {
        padding: '0 16px',
        display: isMenuOpen ? 'flex' : 'none',
        marginTop: 0,
      },
    },
    sideBarBottomMenuGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      marginTop: 'auto',

      '@media (max-width: 550px)': {
        padding: '0 16px',
        display: 'none',
        marginTop: 'auto',
        ...(isMenuOpen && { paddingBottom: '16px', display: 'flex' }),
      },
    },
    logoIconContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      marginLeft: '8px',
      alignItems: 'center',

      '@media (max-width: 550px)': {
        padding: '16px',
        marginLeft: 0,
      },
    },
    burgerMenuIcon: {
      display: 'none',
      svg: {
        color: theme.palette.common.white,
      },

      '@media (max-width: 550px)': {
        display: 'flex',
      },
    },
    versionFooter: {
      marginTop: 'auto',
      paddingTop: '12px',
      borderTop: '1px solid rgba(118, 120, 133, 0.4)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '4px',

      '@media (max-width: 550px)': {
        display: 'none',
      },
    },
    versionText: {
      fontFamily: 'Inter',
      fontSize: '11px',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    whatsNewButton: {
      fontFamily: 'Inter',
      fontSize: '11px',
      color: theme.palette.text.secondary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      background: 'none',
      border: 'none',
      padding: 0,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

interface SidebarProps {
  isSidebarCollapsed: boolean;
}

const Sidebar = ({ isSidebarCollapsed }: SidebarProps) => {
  const theme = useTheme();
  const location = useLocation();
  const windowWidth = useWindowWidth();
  const navigate = useNavigate();
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState<boolean>(false);
  const [version, setVersion] = useState<string>('');
  const [isChangelogOpen, setIsChangelogOpen] = useState<boolean>(false);

  const styles = useStyles({ isMenuOpen: isBurgerMenuOpen, isSidebarCollapsed });

  const baseRouteName = location.pathname.split('/')[1];

  const handleOpenBurgerMenu = () => setIsBurgerMenuOpen(!isBurgerMenuOpen);

  const onLinkClick = (path: string) => {
    setIsBurgerMenuOpen(false);
    navigate(path);
  };

  useEffect(() => {
    windowWidth > 550 && setIsBurgerMenuOpen(false);
  }, [windowWidth]);

  useEffect(() => {
    getVersion()
      .then((data) => setVersion(data.version === 'unknown' ? '0.50.1' : data.version))
      .catch(() => setVersion('0.50.1'));
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setIsBurgerMenuOpen(false)}>
      <Box
        className={clsx(
          styles.classes.sideBarWrapper,
          windowWidth > 550
            ? isSidebarCollapsed
              ? styles.classes.sideBarCollapsed
              : styles.classes.sideBarExpanded
            : 'none',
        )}
      >
        <Box className={styles.classes.logoIconContainer}>
          {/* <Link to="/">
            <LogoIcon />
          </Link> */}

          <IconButton
            className={styles.classes.burgerMenuIcon}
            onClick={handleOpenBurgerMenu}
            disableRipple
          >
            {isBurgerMenuOpen ? <ClearIcon /> : <MenuIcon />}
          </IconButton>
        </Box>

        <Box className={styles.classes.sideBarMainMenuGroup}>
          {menuItems.map((item) => (
            <PermissionGuard
              key={item.path}
              moduleName={item.resource ?? ''}
              permission="View"
              fallback={!item.resource ? undefined : null}
            >
              <Box key={item.path}>
                <Box
                  className={clsx(
                    styles.classes.linkHover,
                    item.separator && styles.classes.linkHoverSeparator,
                  )}
                  onClick={() => onLinkClick(item.path)}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    color={theme.palette.primary.light}
                    className={clsx(
                      styles.classes.menuItem,
                      baseRouteName === item.path && styles.classes.activeMenuItem,
                    )}
                  >
                    <Box
                      className={clsx(
                        styles.classes.iconWrapper,
                        baseRouteName === item.path && styles.classes.activeItemIconWrapper,
                      )}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      className={clsx(
                        baseRouteName === item.path
                          ? styles.classes.activeLink
                          : styles.classes.defaultLink,
                      )}
                    >
                      {item.linkName}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </PermissionGuard>
          ))}
        </Box>

        {!isSidebarCollapsed && (
          <Box className={styles.classes.versionFooter}>
            <Typography className={styles.classes.versionText}>
              {version ? `v${version}` : '—'}
            </Typography>
            <Typography
              component="button"
              className={styles.classes.whatsNewButton}
              onClick={() => setIsChangelogOpen(true)}
            >
              What&apos;s new
            </Typography>
          </Box>
        )}

        <ChangelogPopup open={isChangelogOpen} onClose={() => setIsChangelogOpen(false)} />

        {/* <Box className={styles.classes.sideBarBottomMenuGroup}>
          {menuBottomItems.map((item, idx) => (
            <Box key={idx}>
              <Box onClick={() => onLinkClick(item.path)} className={styles.classes.linkHover}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  color={theme.palette.primary.light}
                  className={clsx(
                    styles.classes.menuItem,
                    baseRouteName === item.path && styles.classes.activeMenuItem,
                  )}
                >
                  <Box
                    className={clsx(
                      styles.classes.iconWrapper,
                      baseRouteName === item.path && styles.classes.activeItemIconWrapper,
                    )}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    className={clsx(
                      baseRouteName === item.path
                        ? styles.classes.activeLink
                        : styles.classes.defaultLink,
                    )}
                  >
                    {item.linkName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}

          <Box className={clsx(styles.classes.linkHover)}>
            <Box
              display="flex"
              alignItems="center"
              gap="8px"
              color={theme.palette.primary.light}
              className={clsx(styles.classes.menuItem)}
              onClick={onLogOut}
            >
              <Box className={styles.classes.iconWrapper}>
                <LogoutIcon />
              </Box>
              <Typography className={clsx(styles.classes.defaultLink)}>Sign Out</Typography>
            </Box>
          </Box>
        </Box> */}
      </Box>
    </ClickAwayListener>
  );
};

export default Sidebar;